import React from 'react';
import {
  Paper,
  Typography,
  Box,
  TextField,
  Select,
  MenuItem
} from '@mui/material';
import FormField from './FormField';
import { standardInputSx } from '../utils/hubsHelpers';

const SeoCard = ({
  metaTitle,
  setMetaTitle,
  indexSetting,
  setIndexSetting,
  metaDescription,
  setMetaDescription,
  canonicalUrl,
  targetKeyword,
  setTargetKeyword,
  pageSlug,
  setLocks,
  showSnackbar
}) => {
  return (
    <Paper
      elevation={0}
      sx={{
        backgroundColor: '#FFFFFF',
        border: '1px solid #E2E8F0',
        borderRadius: '8px',
        p: '14px 16px',
        mb: 1.75,
        boxShadow: 'none'
      }}
    >
      <Typography variant="subtitle1" sx={{ fontSize: 13.5, fontWeight: 600, color: '#1E293B', mb: 0.35, display: 'flex', alignItems: 'center' }}>
        SEO{' '}
        <Box component="span" sx={{ backgroundColor: '#803927', color: '#fff', fontSize: 9.5, px: '5px', py: '1px', borderRadius: '4px', fontWeight: 600, ml: 0.75 }}>
          NEW
        </Box>
      </Typography>
      <Typography variant="body2" sx={{ color: '#64748B', fontSize: 11, mb: 1.25, lineHeight: 1.4 }}>
        What Google shows in search results. Enter custom SEO meta tags. The preview below updates as you type.
      </Typography>

      {/* Row 1: Meta Title & Index Setting */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.25, mb: 1.25 }}>
        <FormField label="Meta title *">
          <Box sx={{ position: 'relative' }}>
            <TextField
              size="small"
              fullWidth
              value={metaTitle}
              inputProps={{ maxLength: 70 }}
              onChange={(e) => {
                setMetaTitle(e.target.value);
                setLocks((prev) => ({ ...prev, meta: true }));
              }}
              sx={{ ...standardInputSx, '& .MuiOutlinedInput-input': { pr: 7 } }}
            />
            <Box
              sx={{
                position: 'absolute',
                right: 12,
                top: 10,
                fontSize: 10.5,
                fontWeight: 500,
                color: metaTitle.length >= 50 && metaTitle.length <= 60 ? '#2E7D32' : (metaTitle.length > 60 ? '#DC2626' : '#D97706'),
                pointerEvents: 'none'
              }}
            >
              {metaTitle.length}/60
            </Box>
          </Box>
        </FormField>

        <FormField label="Index setting">
          <Select
            size="small"
            fullWidth
            value={indexSetting}
            onChange={(e) => setIndexSetting(e.target.value)}
            sx={standardInputSx}
          >
            <MenuItem value="Index, follow">Index, follow</MenuItem>
            <MenuItem value="Noindex, follow">Noindex, follow</MenuItem>
          </Select>
        </FormField>
      </Box>

      {/* Row 2: Meta description */}
      <FormField label="Meta description *" sx={{ mb: 1.25 }}>
        <Box sx={{ position: 'relative' }}>
          <TextField
            size="small"
            fullWidth
            multiline
            rows={2}
            value={metaDescription}
            inputProps={{ maxLength: 180 }}
            onChange={(e) => {
              setMetaDescription(e.target.value);
              setLocks((prev) => ({ ...prev, metaD: true }));
            }}
            sx={{
              ...standardInputSx,
              '& .MuiOutlinedInput-root': {
                ...standardInputSx['& .MuiOutlinedInput-root'],
                height: 'auto',
                minHeight: 60,
                py: '8px'
              },
              '& .MuiOutlinedInput-input': {
                pr: 7
              }
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              right: 12,
              bottom: 8,
              fontSize: 10.5,
              fontWeight: 500,
              color: metaDescription.length >= 150 && metaDescription.length <= 160 ? '#2E7D32' : (metaDescription.length > 160 ? '#DC2626' : '#D97706'),
              pointerEvents: 'none'
            }}
          >
            {metaDescription.length}/160
          </Box>
        </Box>
      </FormField>

      {/* Row 3: Canonical URL & Target Keyword */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.25, mb: 1.25 }}>
        <FormField
          label="Canonical URL"
          helperText={
            <span>
              Automatic, same as the Page URL.{' '}
              <Box
                component="span"
                onClick={() => showSnackbar('Override: type a different URL only when this page duplicates another one', 'info')}
                sx={{ color: '#803927', fontWeight: 600, cursor: 'pointer' }}
              >
                Override
              </Box>
            </span>
          }
        >
          <TextField
            size="small"
            fullWidth
            value={canonicalUrl}
            InputProps={{ readOnly: true }}
            sx={{
              ...standardInputSx,
              '& .MuiOutlinedInput-root': {
                ...standardInputSx['& .MuiOutlinedInput-root'],
                backgroundColor: '#F8FAFC'
              }
            }}
          />
        </FormField>

        <FormField label="Target keyword">
          <TextField
            size="small"
            fullWidth
            value={targetKeyword}
            onChange={(e) => setTargetKeyword(e.target.value)}
            sx={standardInputSx}
          />
        </FormField>
      </Box>

      {/* Google SERP Preview */}
      <Paper
        elevation={0}
        sx={{
          border: '1px solid #E2E8F0',
          borderRadius: '8px',
          p: '10px 12px',
          backgroundColor: '#FFFFFF',
          mt: 1.25
        }}
      >
        <Typography sx={{ color: '#202124', fontSize: 12 }}>
          healatindia.com{pageSlug ? pageSlug.replace(/\//g, ' › ') : ''}
        </Typography>
        <Typography sx={{ color: '#1A0DAB', fontSize: 15, fontWeight: 500, my: 0.25 }}>
          {metaTitle || 'Page Title'}
        </Typography>
        <Typography sx={{ color: '#475569', fontSize: 12.5, lineHeight: 1.4 }}>
          {metaDescription || 'Page description will appear here...'}
        </Typography>
      </Paper>
    </Paper>
  );
};

export default React.memo(SeoCard);
